// Vista de una página de servicio lista para pintar: el contenido efectivo si llegó (editado o por
// defecto, con HTML saneado) y, si no (p. ej. falló la lectura), el texto por defecto en texto plano.

import type { ContenidoHome } from "@/content/pages/home";
import { markdownATextoPlano } from "./texto-plano";
import type { BloquePublico, ContenidoServicio, PageContentResolved } from "./tipos";

/** Texto con HTML opcional: si `html` es null se pinta `texto` tal cual. */
export interface TextoRico {
  html: string | null;
  texto: string;
}

export interface VistaServicio {
  eyebrow: string;
  h1: string;
  intro: string;
  description: TextoRico & { articulo: boolean };
  /** null si no hay nota (vacía). */
  note: TextoRico | null;
  includes: TextoRico[];
  audience: TextoRico[];
  projectTypes: { title: string; desc: TextoRico }[];
  benefits: { title: string; desc: TextoRico }[];
  faqs: { q: string; a: TextoRico }[];
}

type De<T extends BloquePublico["tipo"]> = Extract<BloquePublico, { tipo: T }>;

function bloque<T extends BloquePublico["tipo"]>(
  pc: PageContentResolved,
  key: string,
  tipo: T,
): De<T> | null {
  const b = pc[key];
  return b && b.tipo === tipo ? (b as De<T>) : null;
}

const plano = (md: string): TextoRico => ({ html: null, texto: markdownATextoPlano(md) });

export function vistaServicio(
  base: ContenidoServicio,
  pc: PageContentResolved = {},
): VistaServicio {
  const texto = (key: string, def: string) => bloque(pc, key, "texto")?.texto ?? def;

  const desc = bloque(pc, "description", "markdown");
  const nota = bloque(pc, "note", "markdown");
  const lista = (key: string, def: string[]): TextoRico[] =>
    bloque(pc, key, "lista")?.items.map((i) => ({ html: i.html, texto: i.texto })) ??
    def.map(plano);
  const tarjetas = (key: string, def: ContenidoServicio["benefits"]) =>
    bloque(pc, key, "tarjetas")?.items.map((i) => ({
      title: i.title,
      desc: { html: i.descHtml, texto: i.descTexto },
    })) ?? def.map((i) => ({ title: i.title, desc: plano(i.desc) }));
  const faqs = bloque(pc, "faqs", "faqs");

  return {
    eyebrow: texto("eyebrow", base.eyebrow),
    h1: texto("h1", base.h1),
    intro: texto("intro", base.intro),
    description: desc
      ? { html: desc.html, texto: desc.texto, articulo: desc.articulo === true }
      : { ...plano(base.description), articulo: base.descriptionLayout === "articulo" },
    note: nota
      ? nota.html
        ? { html: nota.html, texto: nota.texto }
        : null
      : base.note
        ? plano(base.note)
        : null,
    includes: lista("includes", base.includes),
    audience: lista("audience", base.audience),
    projectTypes: tarjetas("projectTypes", base.projectTypes),
    benefits: tarjetas("benefits", base.benefits),
    faqs:
      faqs?.items.map((i) => ({ q: i.q, a: { html: i.aHtml, texto: i.aTexto } })) ??
      base.faqs.map((i) => ({ q: i.q, a: plano(i.a) })),
  };
}

/** FAQ en texto plano para los datos estructurados (JSON-LD). */
export function faqsPlanas(v: VistaServicio): { q: string; a: string }[] {
  return v.faqs.map((f) => ({ q: f.q, a: f.a.texto }));
}

export interface VistaHome {
  intro: TextoRico;
  proceso: { n: string; title: string; desc: TextoRico }[];
  faqs: { q: string; a: TextoRico }[];
}

export function vistaHome(base: ContenidoHome, pc: PageContentResolved = {}): VistaHome {
  const intro = bloque(pc, "intro", "markdown");
  const proceso = bloque(pc, "proceso", "tarjetas");
  const faqs = bloque(pc, "faqs", "faqs");
  const num = (i: number) => String(i + 1).padStart(2, "0");
  return {
    intro: intro ? { html: intro.html || null, texto: intro.texto } : plano(base.intro),
    proceso:
      proceso?.items.map((i, k) => ({
        n: num(k),
        title: i.title,
        desc: { html: i.descHtml, texto: i.descTexto },
      })) ?? base.proceso.map((i, k) => ({ n: num(k), title: i.title, desc: plano(i.desc) })),
    faqs:
      faqs?.items.map((i) => ({ q: i.q, a: { html: i.aHtml, texto: i.aTexto } })) ??
      base.faqs.map((i) => ({ q: i.q, a: plano(i.a) })),
  };
}
